import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useGetProductGroupParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type { SalesRule } from 'Services/ProductManagement/Features/SalesRule/types'
import { useGetSingleZoneDefinitionByPtaQuery } from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import { getValueFromOptions } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import theme from 'Util/theme'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { t } from 'ttag'

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
  border-bottom: 0;
`

const StyledAccordionSummary = styled(AccordionSummary)`
  max-height: 40px;
  &.Mui-expanded {
    min-height: 48px;
  }
`

interface SalesRuleZoneRequirementsProps {
  salesRule: SalesRule
}

export function SalesRuleZoneRequirements({ salesRule }: Readonly<SalesRuleZoneRequirementsProps>) {
  const { data: zoneDefinition, isLoading: isZoneDefinitionLoading } =
    useGetSingleZoneDefinitionByPtaQuery(undefined, {
      refetchOnMountOrArgChange: true
    })

  const { data: productGroupParameterGroup, isLoading: isProductGroupLoading } =
    useGetProductGroupParameterGroupQuery()

  const productGroupOptions =
    productGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const isLoading = isZoneDefinitionLoading || isProductGroupLoading

  return (
    <WithLoader loading={isLoading}>
      <Box
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
        component={Paper}
      >
        {salesRule.zone_requirements.map((zoneRequirement, index) => (
          <Accordion
            key={`${zoneRequirement.zone_map.join('')}-${index}`}
            sx={theme => ({
              ...(theme.palette.mode === 'light' && { background: 'white' })
            })}
          >
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel-${index}-content`}
              id={`panel-${index}-header`}
              sx={{
                maxHeight: '40px',
                '&.Mui-expanded': { maxHeight: '40px', minHeight: '48px' }
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {t`Zones`}:{' '}
                {zoneRequirement.zone_map
                  .map(zone => getTranslation(zoneDefinition?.translations[zone], 'fi').value)
                  .join('')}
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Paper>
                <TableContainer>
                  <Table size="small" aria-label={t`Details table`}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          <Typography
                            variant="overline"
                            sx={{ letterSpacing: '1px', fontSize: '12px' }}
                          >{t`Product type`}</Typography>
                        </StyledTableCell>
                        <TableCell sx={{ borderBottom: 0 }}>
                          <Typography
                            variant="overline"
                            sx={{ letterSpacing: '1px', fontSize: '12px' }}
                          >{t`Zones`}</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {zoneRequirement.required_one_of_existing_products.map(
                        (productType, index) => (
                          <StyledTableRow
                            key={`${productType.product_group}-${index}`}
                            sx={{
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? theme.palette.background.paper
                                  : theme.palette.background.default
                            }}
                          >
                            <StyledTableCell>
                              {getValueFromOptions(productType.product_group, productGroupOptions)}
                            </StyledTableCell>
                            <TableCell sx={{ borderBottom: 0 }}>
                              {productType.zone_map
                                .map(
                                  zone =>
                                    getTranslation(zoneDefinition?.translations[zone], 'fi').value
                                )
                                .join('')}
                            </TableCell>
                          </StyledTableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </WithLoader>
  )
}
